import { BadgeImagePlacementValues, BadgeTypeValues, CartLineModel } from "@insite/client-framework/Types/ApiModels";
import { ProductImageBadgesStyles } from "@insite/content-library/Components/ProductImageBadges";
import { ProductTextBadgesStyles } from "@insite/content-library/Components/ProductTextBadges";
import { GridItemProps } from "@insite/mobius/GridItem/GridItem";
import { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";

export type CartLineBadgesProps = {
    showPrice?: boolean;
    showImageBadges?: boolean;
    maximumNumberOfImageBadges?: number;
    showTextBadges?: boolean;
    maximumNumberOfTextBadges?: number;
};

export type CartLineCardBadgedStyles = {
    imageBadgeGridItem?: GridItemProps;
    textBadgeGridItem?: GridItemProps;
    badgeAreaWrapper?: InjectableCss;
    badgeTextWrapper?: InjectableCss;
    badgeImageWrapper?: InjectableCss;
    badgeText?: TypographyPresentationProps;
    badgeImageProps?: JSX.IntrinsicElements["img"];
    textBadgeStyles?: ProductTextBadgesStyles;
    imageBadgeStyles?: ProductImageBadgesStyles;
};

interface WidgetBadgesProps {
    cartLine: CartLineModel;
    showTextBadge?: boolean;
    showImageBadge?: boolean;
    styles?: { badgeTextWrapper?: InjectableCss; badgeImageWrapper?: InjectableCss };
}

interface UseWidgetBadgesProps {
    cartLine: CartLineModel;
    showTextBadges?: boolean;
    showImageBadges?: boolean;
}

/**
 * Manages the positioning for badges that overlay a product image.
 * @param props
 */
const useOverlayCartLineBadge = ({ cartLine, showTextBadge, showImageBadge, styles }: WidgetBadgesProps) => {
    let showOverlayBadge = false;
    let overlayBadge = null;
    let overlayBadgeWrapperStyles = null;
    let badges = [];

    if (cartLine.properties.product_badges && cartLine.properties.product_badges.length > 0) {
        // cartLine.properties.product_badges comes as a string, so we need to parse it into an object array
        badges = JSON.parse(cartLine.properties.product_badges);

        let visibleBadges = badges.filter((badge: { displayOnProductImages: any }) => badge.displayOnProductImages);

        if (!showImageBadge && visibleBadges.length > 0) {
            visibleBadges = visibleBadges.filter(
                (badge: { badgeType: BadgeTypeValues }) => badge.badgeType !== BadgeTypeValues.Image,
            );
        }

        if (!showTextBadge && visibleBadges.length > 0) {
            visibleBadges = visibleBadges.filter(
                (badge: { badgeType: BadgeTypeValues }) => badge.badgeType !== BadgeTypeValues.Text,
            );
        }

        if (visibleBadges.length > 0) {
            overlayBadge = visibleBadges[0];
            showOverlayBadge = true;
            overlayBadgeWrapperStyles =
                overlayBadge.badgeType === "Image" ? styles?.badgeImageWrapper : styles?.badgeTextWrapper;
            const width = overlayBadge.badgeType === "Image" ? 25 : 50;
            let positioning = "";

            // everything with be a few percentages off of the absolute edge
            if (overlayBadge.largeImagePlacement) {
                switch (overlayBadge.largeImagePlacement) {
                    case BadgeImagePlacementValues.TopLeft:
                        positioning = "left: 2%; top: 2%;";
                        break;
                    case BadgeImagePlacementValues.TopCenter:
                        positioning = ` left: 50%; transform: translateX(-50%); top: 2%;`;
                        break;
                    case BadgeImagePlacementValues.TopRight:
                        positioning = "right: 2%; top: 2%;";
                        break;
                    case BadgeImagePlacementValues.BottomLeft:
                        positioning = "left: 2%; bottom: 2%;";
                        break;
                    case BadgeImagePlacementValues.BottomCenter:
                        positioning = `left: ${(100 - width) / 2}%; bottom: 2%;`;
                        break;
                    case BadgeImagePlacementValues.BottomRight:
                        positioning = "right: 2%; bottom: 2%;";
                        break;
                    default:
                        positioning = "left: 2%; top: 2%;";
                }

                if (overlayBadgeWrapperStyles?.css) {
                    // insert badge positional data
                    overlayBadgeWrapperStyles.css = overlayBadgeWrapperStyles.css.concat(positioning);
                }
            }
        }
    }

    return {
        showOverlayBadge,
        overlayBadge,
        overlayBadgeWrapperStyles,
    };
};

export const useWidgetCartLineBadges = ({ cartLine, showTextBadges, showImageBadges }: UseWidgetBadgesProps) => {
    let imageBadges = null;
    let badges = [];

    if (cartLine.properties.product_badges && cartLine.properties.product_badges.length > 0) {
        // cartLine.properties.product_badges comes as a string, so we need to parse it into an object array
        badges = JSON.parse(cartLine.properties.product_badges);
    }

    if (showImageBadges && badges) {
        imageBadges = badges.filter(
            (o: { displayOnBadgeWidget: any; badgeType: BadgeTypeValues; largeImageBadgePath: any }) =>
                o.displayOnBadgeWidget && o.badgeType === BadgeTypeValues.Image && o.largeImageBadgePath,
        );
    }

    let textBadges = null;
    if (showTextBadges && badges) {
        textBadges = badges.filter(
            (o: { displayOnBadgeWidget: any; badgeType: BadgeTypeValues; displayText: any }) =>
                o.displayOnBadgeWidget && o.badgeType === BadgeTypeValues.Text && o.displayText,
        );
    }

    return {
        imageBadges,
        textBadges,
    };
};

// eslint-disable-next-line spire/export-styles
export default useOverlayCartLineBadge;
